import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Eye,
  Check,
  X,
  Package,
  Percent,
  DollarSign,
  Truck,
  Receipt,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  Layers,
  ChevronRight
} from 'lucide-react';
import './GestionFinanciere.css';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';

const API_URL = import.meta.env.VITE_API_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-TN', {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  }).format(Number(value) || 0);
};

const round3 = (val) => Math.round((Number(val) || 0) * 1000) / 1000;

const GestionFinanciere = ({ clientId, dossierId }) => {
  const { hasPermission } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Core Data
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState('');
  const [packAffecte, setPackAffecte] = useState(null);
  const [produitsClient, setProduitsClient] = useState([]);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [allDocuments, setAllDocuments] = useState([]);
  const [resumePaiement, setResumePaiement] = useState(null);

  // Logistics for BL creation
  const [vehicules, setVehicules] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [selectedVehiculeId, setSelectedVehiculeId] = useState('');
  const [selectedEquipeId, setSelectedEquipeId] = useState('');
  const [selectedRole, setSelectedRole] = useState('chefEquipe');

  // Remise Configuration State
  const [remiseMode, setRemiseMode] = useState('pourcentage'); // 'pourcentage' | 'montant_final'
  const [remiseValeurInput, setRemiseValeurInput] = useState('');
  const [devisMode, setDevisMode] = useState('detaille'); // Fallback global
  const [pdfDisplayModesMap, setPdfDisplayModesMap] = useState({});

  // Modals
  const [showPackModal, setShowPackModal] = useState(false);
  const [showBLModal, setShowBLModal] = useState(false);
  const [submittingBL, setSubmittingBL] = useState(false);

  // Initial Data Fetching
  useEffect(() => {
    if (clientId) {
      chargerTout();
    }
  }, [clientId, dossierId]);

  const chargerTout = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        chargerWorkflow(),
        chargerProduitsClient(),
        chargerTousLesDocuments(),
        chargerResumePaiement(),
        chargerPacks(),
        chargerLogistique()
      ]);
    } catch (err) {
      console.error('Erreur chargement finance:', err);
      setError('Erreur lors du chargement des données financières');
    } finally {
      setLoading(false);
    }
  };

  const chargerPacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/packs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const packsData = res.data?.packs || (Array.isArray(res.data) ? res.data : []);
      setPacks(packsData);
    } catch (err) {
      console.error('Erreur chargement packs:', err);
    }
  };

  const chargerLogistique = async () => {
    try {
      const token = localStorage.getItem('token');
      const [vehRes, eqRes] = await Promise.all([
        axios.get(`${API_URL}/vehicules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/equipes-installation`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const vehList = Array.isArray(vehRes.data) ? vehRes.data : (vehRes.data?.data || []);
      const eqList = Array.isArray(eqRes.data) ? eqRes.data : (eqRes.data?.data || []);
      setVehicules(vehList);
      setEquipes(eqList);
      if (vehList.length > 0) setSelectedVehiculeId(vehList[0]._id);
      if (eqList.length > 0) setSelectedEquipeId(eqList[0]._id);
    } catch (err) {
      console.error('Erreur logistique:', err);
    }
  };

  const chargerWorkflow = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/documents-financiers/client/${clientId}/workflow`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setWorkflowStatus(response.data.status);
  };

  const chargerProduitsClient = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/documents-financiers/client/${clientId}/produits`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setProduitsClient(response.data.produits || []);
  };

  const chargerTousLesDocuments = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/documents-financiers/client/${clientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAllDocuments(response.data.documents || []);
  };

  const chargerResumePaiement = async () => {
    if (!clientId || !dossierId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientData = response.data?.data || response.data;
      const dossiers = clientData?.dossiers || [];
      const dossier = dossiers.find((d) => String(d._id) === String(dossierId));

      if (dossier) {
        if (dossier.packId) {
          const matchedPack = packs.find(p => String(p._id) === String(dossier.packId));
          setPackAffecte(matchedPack || { _id: dossier.packId, nom: dossier.packNom || 'Pack Affecté' });
        }

        const autofinancement = dossier.autofinancement || {};
        const montantComptant = Number(dossier.montantComptant) || Number(autofinancement.montantComptantInitial) || 0;
        const montantComptantPaye = Number(autofinancement.montantComptantPaye) || 0;
        const montantCredit = Number(dossier.montantCredit) || Number(autofinancement.montantTotal) || 0;
        const montantCreditPaye = Number(autofinancement.montantPaye) || 0;
        const montantSteg = Number(dossier.montantSteg || 0);
        const totalPaiement = Number(dossier.montantTotalPaiement) || Number((montantSteg + montantComptant + montantCredit).toFixed(3));
        const totalPaye = (montantComptantPaye + montantCreditPaye + (dossier.stegPaye ? montantSteg : 0));
        const totalRestant = Math.max(0, totalPaiement - totalPaye);

        setResumePaiement({
          montantTotal: totalPaiement,
          totalPaye,
          resteAPayer: round3(totalRestant),
          dossier
        });
      }
    } catch (err) {
      console.error('Erreur résumé paiement:', err);
    }
  };

  // Synchroniser la configuration du Devis existant s'il y en a un
  const devisExistant = useMemo(() => {
    return workflowStatus?.devis?.dernier || allDocuments.find(d => d.type === 'devis' && d.statut !== 'supprime');
  }, [workflowStatus, allDocuments]);

  const blExistant = useMemo(() => {
    return workflowStatus?.bon_livraison?.dernier || allDocuments.find(d => d.type === 'bon_livraison' && d.statut !== 'supprime');
  }, [workflowStatus, allDocuments]);

  const factureExistante = useMemo(() => {
    return workflowStatus?.facture?.dernier || allDocuments.find(d => d.type === 'facture' && d.statut !== 'supprime');
  }, [workflowStatus, allDocuments]);

  // Calcul du montant initial brut du dossier
  const montantInitialBrut = useMemo(() => {
    if (devisExistant && devisExistant.totalHTInitial > 0) {
      return round3(devisExistant.totalHTInitial * 1.19); // TTC Brut
    }
    if (produitsClient && produitsClient.length > 0) {
      const sum = produitsClient.reduce((acc, p) => acc + (Number(p.prixTotal) || (Number(p.prixUnitaire) * Number(p.quantite)) || 0), 0);
      return round3(sum * 1.19);
    }
    if (packAffecte && packAffecte.prixFinal) {
      return round3(packAffecte.prixFinal);
    }
    return 0;
  }, [devisExistant, produitsClient, packAffecte]);

  // Extraction des catégories d'équipements actives pour la configuration PDF
  const activeCategories = useMemo(() => {
    const categoriesSet = new Set();
    
    if (produitsClient && Array.isArray(produitsClient)) {
      produitsClient.forEach((p) => {
        const cat = p.categorieNom || p.produitId?.categorieId?.label || p.produitId?.categorie?.label || p.designation || p.nomProduit;
        if (cat && String(cat).trim()) {
          categoriesSet.add(String(cat).trim());
        }
      });
    }
    
    if (packAffecte && Array.isArray(packAffecte.categories)) {
      packAffecte.categories.forEach((cat) => {
        const catName = cat.categorieId?.label || cat.label || cat.categorieNom;
        if (catName && String(catName).trim()) {
          categoriesSet.add(String(catName).trim());
        }
      });
    }

    if (categoriesSet.size === 0) {
      ['Coffret AC', 'Coffret DC+', 'Module photovoltaïque Luxor', 'Onduleur', 'Structure', 'Câblage', 'Protection'].forEach(c => categoriesSet.add(c));
    }

    return Array.from(categoriesSet);
  }, [produitsClient, packAffecte]);

  // Initialisation de la valeur de remise et des modes PDF par catégorie depuis le devis existant
  useEffect(() => {
    if (devisExistant) {
      if (devisExistant.remise > 0) {
        setRemiseMode('pourcentage');
        setRemiseValeurInput(String(devisExistant.remise));
      }
      if (Array.isArray(devisExistant.pdfDisplayModes) && devisExistant.pdfDisplayModes.length > 0) {
        const map = {};
        devisExistant.pdfDisplayModes.forEach(item => {
          if (item.categorieNom) map[item.categorieNom] = item.mode;
        });
        setPdfDisplayModesMap(map);
      } else if (devisExistant.devisMode) {
        const map = {};
        activeCategories.forEach(cat => {
          map[cat] = devisExistant.devisMode;
        });
        setPdfDisplayModesMap(map);
      }
    }
  }, [devisExistant, activeCategories]);

  const handleToggleCategoryMode = (catName, newMode) => {
    setPdfDisplayModesMap(prev => ({
      ...prev,
      [catName]: newMode
    }));
  };

  const handleSetAllCategoryModes = (targetMode) => {
    const updatedMap = {};
    activeCategories.forEach(cat => {
      updatedMap[cat] = targetMode;
    });
    setPdfDisplayModesMap(updatedMap);
  };

  // Calculs réactifs de la remise
  const calculsFinanciers = useMemo(() => {
    const initial = montantInitialBrut;
    let remisePct = 0;
    let montantRemise = 0;
    let montantFinal = initial;

    if (initial > 0 && remiseValeurInput !== '') {
      const val = Number(remiseValeurInput) || 0;
      if (remiseMode === 'pourcentage') {
        remisePct = Math.min(100, Math.max(0, val));
        montantRemise = round3(initial * (remisePct / 100));
        montantFinal = round3(initial - montantRemise);
      } else {
        // Montant final souhaité
        montantFinal = Math.max(0, val);
        if (montantFinal <= initial) {
          montantRemise = round3(initial - montantFinal);
          remisePct = round3((montantRemise / initial) * 100);
        } else {
          montantFinal = initial;
          montantRemise = 0;
          remisePct = 0;
        }
      }
    }

    return {
      montantInitial: initial,
      remisePct,
      montantRemise,
      montantFinal
    };
  }, [montantInitialBrut, remiseMode, remiseValeurInput]);

  // --- ACTIONS ---

  // 1. Affectation d'un Pack
  const handleAffecterPack = async () => {
    if (!selectedPackId) {
      setError('Veuillez sélectionner un pack commercial');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/clients/${clientId}/dossiers/${dossierId}/pack`, {
        packId: selectedPackId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const packObj = packs.find(p => String(p._id) === String(selectedPackId));
      setPackAffecte(packObj || { _id: selectedPackId, nom: 'Pack Affecté' });
      setSuccess(res.data.message || 'Pack affecté avec succès !');

      await chargerProduitsClient();
      await chargerResumePaiement();

      Swal.fire({
        title: 'Pack Affecté !',
        text: `Le pack commercial a été affecté aux produits du dossier client.`,
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (err) {
      console.error('Erreur affectation pack:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'affectation du pack');
    } finally {
      setLoading(false);
    }
  };

  // 2. Générer / Mettre à jour le Devis
  const handleSoumettreDevis = async () => {
    const isEdit = !!devisExistant;
    const finalAmount = calculsFinanciers.montantFinal;

    // Alerte confirmation SweetAlert2 si changement de prix impactant le dossier
    if (resumePaiement && Math.abs((resumePaiement.montantTotal || 0) - finalAmount) > 0.05) {
      const result = await Swal.fire({
        title: 'Confirmation du montant du devis',
        html: `<div style="font-size:0.95rem;color:#1e293b;text-align:left;">
          <p>Le montant total du devis et du dossier passera de :</p>
          <p style="font-weight:700;font-size:1.1rem;color:#e11d48;">${formatCurrency(resumePaiement.montantTotal)} → ${formatCurrency(finalAmount)}</p>
          <p style="margin-top:10px;font-size:0.85rem;color:#64748b;">
            Les encaissements déjà enregistrés (<strong>${formatCurrency(resumePaiement.totalPaye)}</strong>) seront scrupuleusement conservés.
          </p>
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, valider et enregistrer',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#2563eb',
        cancelButtonColor: '#64748b'
      });
      if (!result.isConfirmed) return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const pdfDisplayModesArray = activeCategories.map(cat => ({
        categorieNom: cat,
        mode: pdfDisplayModesMap[cat] || 'detaille'
      }));

      const payload = {
        clientId,
        dossierId,
        type: 'devis',
        remise: calculsFinanciers.remisePct,
        prixTotalProduits: calculsFinanciers.montantFinal,
        pdfDisplayModes: pdfDisplayModesArray,
        packApplique: packAffecte ? JSON.stringify({ packId: packAffecte._id, nom: packAffecte.nom }) : null
      };

      if (isEdit) {
        await axios.put(`${API_URL}/documents-financiers/${devisExistant._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Devis mis à jour avec succès');
      } else {
        await axios.post(`${API_URL}/documents-financiers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Devis créé avec succès');
      }

      await chargerWorkflow();
      await chargerTousLesDocuments();
      await chargerResumePaiement();

      Swal.fire({
        title: isEdit ? 'Devis mis à jour !' : 'Devis généré !',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (err) {
      console.error('Erreur devis:', err);
      setError(err.response?.data?.message || 'Erreur lors de la génération du devis');
    } finally {
      setLoading(false);
    }
  };

  // 3. Valider le Devis
  const handleValiderDevis = async () => {
    if (!devisExistant) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/documents-financiers/${devisExistant._id}/valider`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Devis validé avec succès');
      await chargerWorkflow();
      await chargerTousLesDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur validation devis');
    }
  };

  // 4. Soumettre BL (Bon de Livraison)
  const handleSoumettreBL = async (e) => {
    e.preventDefault();
    if (!selectedVehiculeId || !selectedEquipeId) {
      setError('Veuillez sélectionner un véhicule et une équipe d\'installation');
      return;
    }
    setSubmittingBL(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        clientId,
        dossierId,
        type: 'bon_livraison',
        description: 'Bon de livraison matériel client',
        vehiculeId: selectedVehiculeId,
        chauffeurMembre: {
          equipeId: selectedEquipeId,
          role: selectedRole
        }
      };

      const res = await axios.post(`${API_URL}/documents-financiers`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowBLModal(false);
      setSuccess('Bon de Livraison créé avec succès !');
      await chargerWorkflow();
      await chargerTousLesDocuments();

      if (res.data?.document?._id) {
        window.open(`${API_URL}/documents-financiers/${res.data.document._id}/pdf?token=${token}`, '_blank');
      }
    } catch (err) {
      console.error('Erreur création BL:', err);
      const errData = err.response?.data;
      if (errData?.produitsNonLivrables && Array.isArray(errData.produitsNonLivrables)) {
        const msg = errData.produitsNonLivrables.map(p => `• ${p.nomProduit || 'Produit'} (Refusé : stock insuffisant)`).join('<br/>');
        Swal.fire({
          title: 'Stock Physique Insuffisant',
          html: `<div style="text-align:left;font-size:0.9rem;">${msg}</div>`,
          icon: 'error'
        });
      } else {
        setError(errData?.message || 'Erreur lors de la création du BL');
      }
    } finally {
      setSubmittingBL(false);
    }
  };

  // 5. Générer Facture
  const handleGenererFacture = async () => {
    if (!blExistant) {
      setError('Un Bon de Livraison doit exister avant de générer la facture');
      return;
    }
    if (resumePaiement && resumePaiement.resteAPayer > 0.01) {
      Swal.fire({
        title: 'Facture Bloquée',
        text: `Le dossier doit être totalement payé avant de générer la facture (Reste à payer : ${formatCurrency(resumePaiement.resteAPayer)}).`,
        icon: 'warning'
      });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/documents-financiers`, {
        clientId,
        dossierId,
        type: 'facture',
        description: 'Facture finale d\'installation'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Facture générée avec succès');
      await chargerWorkflow();
      await chargerTousLesDocuments();

      if (res.data?.document?._id) {
        window.open(`${API_URL}/documents-financiers/${res.data.document._id}/pdf?token=${token}`, '_blank');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la génération de la facture');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir PDF
  const handleOuvrirPDF = (docId) => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/documents-financiers/${docId}/pdf?token=${token}`, '_blank');
  };

  if (loading && !workflowStatus) {
    return <LoadingSpinner text="Chargement de l'espace financier..." />;
  }

  return (
    <div className="fin-wrapper">
      {/* ALERT BANNERS */}
      {error && (
        <div className="fin-alert fin-alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="fin-alert fin-alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}><X size={16} /></button>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          BLOC 1: PACK CLIENT
      ───────────────────────────────────────────────────────────── */}
      <div className="fin-card">
        <div className="fin-card-header">
          <div className="fin-card-title">
            <Package size={22} className="fin-icon-primary" />
            <h3>Pack Client</h3>
          </div>
          {packAffecte && (
            <span className="fin-badge fin-badge-blue">{packAffecte.puissance || 'Pack Attribué'}</span>
          )}
        </div>

        <div className="fin-card-body">
          {packAffecte ? (
            <div className="fin-pack-info-row">
              <div className="fin-pack-details">
                <h4 className="fin-pack-name">{packAffecte.nom}</h4>
                <p className="fin-pack-sub">
                  Montant initial : <strong>{formatCurrency(montantInitialBrut)}</strong>
                </p>
              </div>

              <div className="fin-pack-actions">
                <button
                  type="button"
                  className="fin-btn fin-btn-secondary"
                  onClick={() => setShowPackModal(true)}
                >
                  <Eye size={16} /> Voir les produits du pack
                </button>
              </div>
            </div>
          ) : (
            <div className="fin-pack-empty-box">
              <AlertTriangle size={24} className="fin-icon-warning" />
              <div className="fin-pack-empty-content">
                <h4>Aucun pack affecté</h4>
                <p>Sélectionnez un pack commercial pour pré-remplir les équipements du dossier client.</p>
                <div className="fin-pack-select-group">
                  <select
                    className="fin-select"
                    value={selectedPackId}
                    onChange={(e) => setSelectedPackId(e.target.value)}
                  >
                    <option value="">-- Choisir un Pack Commercial --</option>
                    {packs.map((p) => {
                      const powerStr = p.puissance ? (String(p.puissance).toLowerCase().includes('kwc') ? p.puissance : `${p.puissance} kWc`) : 'Puissance N/A';
                      const finalPNum = Number(p.prixFinal);
                      const calcPNum = Number(p.prixCalcule);
                      const priceVal = (!isNaN(finalPNum) && finalPNum > 0)
                        ? finalPNum
                        : ((!isNaN(calcPNum) && calcPNum > 0) ? calcPNum : null);
                      const priceStr = priceVal !== null ? `${priceVal.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT` : 'Prix non défini';
                      return (
                        <option key={p._id} value={p._id}>
                          {p.nom} — {powerStr} — {priceStr}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="fin-btn fin-btn-primary"
                    onClick={handleAffecterPack}
                  >
                    Affecter ce pack
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          BLOC 2: GESTION DE LA REMISE & DEVIS
      ───────────────────────────────────────────────────────────── */}
      <div className="fin-card">
        <div className="fin-card-header">
          <div className="fin-card-title">
            <Percent size={22} className="fin-icon-primary" />
            <h3>Remise & Configuration du Devis</h3>
          </div>
        </div>

        <div className="fin-card-body">
          {/* Mode d'affichage PDF par Catégorie */}
          <div className="fin-pdf-categories-box">
            <div className="fin-pdf-categories-header">
              <label><FileText size={16} /> Configuration du niveau de détail PDF par désignation :</label>
              <div className="fin-batch-actions">
                <button
                  type="button"
                  className="fin-btn-xs"
                  onClick={() => handleSetAllCategoryModes('detaille')}
                >
                  Tout Détaillé
                </button>
                <button
                  type="button"
                  className="fin-btn-xs"
                  onClick={() => handleSetAllCategoryModes('simplifie')}
                >
                  Tout Simplifié
                </button>
              </div>
            </div>
            <div className="fin-categories-grid">
              {activeCategories.map((cat) => {
                const currentMode = pdfDisplayModesMap[cat] || 'detaille';
                return (
                  <div key={cat} className="fin-cat-toggle-row">
                    <span className="fin-cat-name" title={cat}>{cat}</span>
                    <div className="fin-toggle-group">
                      <button
                        type="button"
                        className={`fin-toggle-btn ${currentMode === 'detaille' ? 'active-detaille' : ''}`}
                        onClick={() => handleToggleCategoryMode(cat, 'detaille')}
                      >
                        Détaillé
                      </button>
                      <button
                        type="button"
                        className={`fin-toggle-btn ${currentMode === 'simplifie' ? 'active-simplifie' : ''}`}
                        onClick={() => handleToggleCategoryMode(cat, 'simplifie')}
                      >
                        Simplifié
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Mode Remise Selector */}
          <div className="fin-radio-group">
            <label className={`fin-radio-label ${remiseMode === 'pourcentage' ? 'active' : ''}`}>
              <input
                type="radio"
                name="remiseMode"
                value="pourcentage"
                checked={remiseMode === 'pourcentage'}
                onChange={() => setRemiseMode('pourcentage')}
              />
              Pourcentage (%)
            </label>
            <label className={`fin-radio-label ${remiseMode === 'montant_final' ? 'active' : ''}`}>
              <input
                type="radio"
                name="remiseMode"
                value="montant_final"
                checked={remiseMode === 'montant_final'}
                onChange={() => setRemiseMode('montant_final')}
              />
              Montant final souhaité (DT)
            </label>
          </div>

          {/* Saisie Field */}
          <div className="fin-remise-input-box">
            {remiseMode === 'pourcentage' ? (
              <div className="fin-input-wrapper">
                <label>Pourcentage de remise (%) :</label>
                <div className="fin-input-addon">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className="fin-input"
                    placeholder="Ex: 5"
                    value={remiseValeurInput}
                    onChange={(e) => setRemiseValeurInput(e.target.value)}
                  />
                  <span>%</span>
                </div>
              </div>
            ) : (
              <div className="fin-input-wrapper">
                <label>Montant final souhaité (DT) :</label>
                <div className="fin-input-addon">
                  <input
                    type="number"
                    min="0"
                    step="10"
                    className="fin-input"
                    placeholder="Ex: 9500"
                    value={remiseValeurInput}
                    onChange={(e) => setRemiseValeurInput(e.target.value)}
                  />
                  <span>DT</span>
                </div>
              </div>
            )}
          </div>

          {/* Clean Financial Summary Card */}
          <div className="fin-summary-card">
            <div className="fin-summary-row">
              <span>Montant initial brut</span>
              <strong>{formatCurrency(calculsFinanciers.montantInitial)}</strong>
            </div>
            <div className="fin-summary-row fin-text-danger">
              <span>Remise accordée ({calculsFinanciers.remisePct} %)</span>
              <strong>- {formatCurrency(calculsFinanciers.montantRemise)}</strong>
            </div>
            <div className="fin-summary-divider" />
            <div className="fin-summary-row fin-summary-total">
              <span>Montant final devis</span>
              <strong className="fin-total-amount">{formatCurrency(calculsFinanciers.montantFinal)}</strong>
            </div>
          </div>

          {/* Action Button Devis */}
          <div className="fin-devis-action-box">
            <button
              type="button"
              className="fin-btn fin-btn-lg fin-btn-primary"
              onClick={handleSoumettreDevis}
            >
              <FileText size={18} />
              {devisExistant ? 'Mettre à jour le devis' : 'Générer le devis'}
            </button>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          BLOC 3: WORKFLOW DOCUMENTAIRE VISUEL
      ───────────────────────────────────────────────────────────── */}
      <div className="fin-card">
        <div className="fin-card-header">
          <div className="fin-card-title">
            <Layers size={22} className="fin-icon-primary" />
            <h3>Workflow Documentaire</h3>
          </div>
        </div>

        <div className="fin-card-body">
          {/* Stepper Grid */}
          <div className="fin-workflow-stepper">

            {/* STEP 1: DEVIS */}
            <div className={`fin-step-card ${devisExistant ? 'completed' : 'pending'}`}>
              <div className="fin-step-header">
                <span className="fin-step-icon"><FileText size={20} /></span>
                <h4>1. Devis Client</h4>
              </div>
              <div className="fin-step-status">
                {devisExistant ? (
                  devisExistant.statut === 'valide' ? (
                    <span className="fin-badge fin-badge-green"><CheckCircle size={14} /> Validé</span>
                  ) : (
                    <span className="fin-badge fin-badge-yellow"><Clock size={14} /> Brouillon</span>
                  )
                ) : (
                  <span className="fin-badge fin-badge-gray">Non créé</span>
                )}
              </div>
              <div className="fin-step-actions">
                {devisExistant && (
                  <>
                    <button type="button" className="fin-btn-sm fin-btn-outline" onClick={() => handleOuvrirPDF(devisExistant._id)}>
                      <Download size={14} /> Devis PDF
                    </button>
                    {devisExistant.statut !== 'valide' && (
                      <button type="button" className="fin-btn-sm fin-btn-success" onClick={handleValiderDevis}>
                        <Check size={14} /> Valider
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="fin-stepper-arrow"><ArrowRight size={20} /></div>

            {/* STEP 2: BL */}
            <div className={`fin-step-card ${blExistant ? 'completed' : 'pending'}`}>
              <div className="fin-step-header">
                <span className="fin-step-icon"><Truck size={20} /></span>
                <h4>2. Bon de Livraison</h4>
              </div>
              <div className="fin-step-status">
                {blExistant ? (
                  blExistant.statutLivraison === 'livre' ? (
                    <span className="fin-badge fin-badge-green"><CheckCircle size={14} /> Livré</span>
                  ) : (
                    <span className="fin-badge fin-badge-blue"><Clock size={14} /> En préparation</span>
                  )
                ) : (
                  <span className="fin-badge fin-badge-gray">Non créé</span>
                )}
              </div>
              <div className="fin-step-actions">
                {blExistant ? (
                  <>
                    <button type="button" className="fin-btn-sm fin-btn-outline" onClick={() => handleOuvrirPDF(blExistant._id)}>
                      <Download size={14} /> Voir BL PDF
                    </button>
                    <button type="button" className="fin-btn-sm fin-btn-primary" onClick={() => setShowBLModal(true)}>
                      <Truck size={14} /> Autre BL
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="fin-btn-sm fin-btn-primary"
                    disabled={!devisExistant}
                    onClick={() => setShowBLModal(true)}
                  >
                    <Truck size={14} /> Créer BL
                  </button>
                )}
              </div>
            </div>

            <div className="fin-stepper-arrow"><ArrowRight size={20} /></div>

            {/* STEP 3: FACTURE */}
            <div className={`fin-step-card ${factureExistante ? 'completed' : (resumePaiement?.resteAPayer > 0.01 ? 'blocked' : 'ready')}`}>
              <div className="fin-step-header">
                <span className="fin-step-icon"><Receipt size={20} /></span>
                <h4>3. Facture</h4>
              </div>
              <div className="fin-step-status">
                {factureExistante ? (
                  <span className="fin-badge fin-badge-green"><CheckCircle size={14} /> Générée</span>
                ) : resumePaiement && resumePaiement.resteAPayer > 0.01 ? (
                  <span className="fin-badge fin-badge-red"><ShieldCheck size={14} /> Bloquée</span>
                ) : (
                  <span className="fin-badge fin-badge-green">Prête</span>
                )}
              </div>

              {/* Message blocage si reste > 0 */}
              {!factureExistante && resumePaiement && resumePaiement.resteAPayer > 0.01 && (
                <p className="fin-blocked-note">
                  En attente du paiement complet (Reste : {formatCurrency(resumePaiement.resteAPayer)})
                </p>
              )}

              <div className="fin-step-actions">
                {factureExistante ? (
                  <button type="button" className="fin-btn-sm fin-btn-outline" onClick={() => handleOuvrirPDF(factureExistante._id)}>
                    <Download size={14} /> Facture PDF
                  </button>
                ) : (
                  <button
                    type="button"
                    className="fin-btn-sm fin-btn-success"
                    disabled={!blExistant || (resumePaiement && resumePaiement.resteAPayer > 0.01)}
                    onClick={handleGenererFacture}
                  >
                    <Receipt size={14} /> Générer Facture
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          BLOC 4: SUIVI DES PAIEMENTS
      ───────────────────────────────────────────────────────────── */}
      <div className="fin-card">
        <div className="fin-card-header">
          <div className="fin-card-title">
            <DollarSign size={22} className="fin-icon-primary" />
            <h3>Suivi des Paiements Dossier</h3>
          </div>
        </div>

        <div className="fin-card-body">
          {resumePaiement ? (
            <div className="fin-payment-summary-box">
              <div className="fin-payment-stat">
                <span>Montant Total Dossier</span>
                <strong>{formatCurrency(resumePaiement.montantTotal)}</strong>
              </div>
              <div className="fin-payment-stat fin-text-green">
                <span>Total Encaissé</span>
                <strong>{formatCurrency(resumePaiement.totalPaye)}</strong>
              </div>
              <div className="fin-payment-stat fin-text-danger">
                <span>Reste à Payer</span>
                <strong>{formatCurrency(resumePaiement.resteAPayer)}</strong>
              </div>

              {/* Progress Bar */}
              <div className="fin-progress-container">
                <div
                  className="fin-progress-bar"
                  style={{
                    width: `${resumePaiement.montantTotal > 0 ? Math.min(100, Math.round((resumePaiement.totalPaye / resumePaiement.montantTotal) * 100)) : 0}%`
                  }}
                />
              </div>
            </div>
          ) : (
            <p className="fin-empty-text">Aucun dossier de paiement actif trouvé.</p>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 1: PRODUITS DU PACK
      ───────────────────────────────────────────────────────────── */}
      {showPackModal && packAffecte && (
        <div className="fin-modal-overlay" onClick={() => setShowPackModal(false)}>
          <div className="fin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Produits du Pack: {packAffecte.nom}</h3>
              <button onClick={() => setShowPackModal(false)}><X size={20} /></button>
            </div>
            <div className="fin-modal-body">
              {packAffecte.categories && packAffecte.categories.length > 0 ? (
                <div className="fin-pack-cats-list">
                  {packAffecte.categories.map((cat, idx) => (
                    <div key={idx} className="fin-pack-cat-item">
                      <h4 className="fin-cat-title">{cat.categorieId?.label || 'Catégorie'}</h4>
                      <ul className="fin-cat-prods">
                        {cat.produits && cat.produits.map((prod, pIdx) => (
                          <li key={pIdx}>
                            <span className="fin-prod-name">{prod.produitId?.nomProduit || prod.nomProduit || 'Produit'}</span>
                            <span className="fin-prod-qte">Quantité : {prod.quantiteRecommandee || 1}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : produitsClient && produitsClient.length > 0 ? (
                <div className="fin-pack-cats-list">
                  <h4 className="fin-cat-title">Articles du Client</h4>
                  <ul className="fin-cat-prods">
                    {produitsClient.map((pc, i) => (
                      <li key={i}>
                        <span className="fin-prod-name">{pc.produitId?.nomProduit || 'Article'}</span>
                        <span className="fin-prod-qte">Quantité : {pc.quantite}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p>Aucun détail de produit disponible pour ce pack.</p>
              )}
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-secondary" onClick={() => setShowPackModal(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL 2: CRÉATION BL LOGISTIQUE
      ───────────────────────────────────────────────────────────── */}
      {showBLModal && (
        <div className="fin-modal-overlay" onClick={() => setShowBLModal(false)}>
          <div className="fin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Préparation du Bon de Livraison (BL)</h3>
              <button onClick={() => setShowBLModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSoumettreBL}>
              <div className="fin-modal-body">
                <div className="fin-form-group">
                  <label>Véhicule de Transport :</label>
                  <select
                    className="fin-select"
                    value={selectedVehiculeId}
                    onChange={(e) => setSelectedVehiculeId(e.target.value)}
                    required
                  >
                    {vehicules.map((v) => (
                      <option key={v._id} value={v._id}>
                        {v.marque} - {v.matricule} ({v.type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="fin-form-group" style={{ marginTop: '15px' }}>
                  <label>Équipe d'Installation :</label>
                  <select
                    className="fin-select"
                    value={selectedEquipeId}
                    onChange={(e) => setSelectedEquipeId(e.target.value)}
                    required
                  >
                    {equipes.map((eq) => (
                      <option key={eq._id} value={eq._id}>
                        {eq.nomEquipe || `Équipe #${eq._id.slice(-4)}`} (Chef: {eq.chefEquipe?.nom || 'Installateur'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="fin-form-group" style={{ marginTop: '15px' }}>
                  <label>Rôle du Chauffeur :</label>
                  <select
                    className="fin-select"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    <option value="chefEquipe">Chef d'Équipe</option>
                    <option value="technicien1">Technicien 1</option>
                    <option value="technicien2">Technicien 2</option>
                  </select>
                </div>
              </div>
              <div className="fin-modal-footer">
                <button type="button" className="fin-btn fin-btn-secondary" onClick={() => setShowBLModal(false)}>Annuler</button>
                <button type="submit" className="fin-btn fin-btn-primary" disabled={submittingBL}>
                  {submittingBL ? 'Génération...' : 'Valider et Générer le BL'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionFinanciere;
